import { useCallback, useMemo, useRef, useState } from 'react';

import {
  CreativityEditor as CreativityEditorCore,
  type CanvasJSON,
  type CreativityEditorHandle,
  type SavedProject,
  C,
  generateId
} from '@doer/frontend-components/creativity-editor';

import { Sidebar } from '../components/sidebar/sidebar';
import { useProjectStorage } from '../hooks/use-project-storage';
import { STANDALONE_RESOLUTIONS } from '../layouts/standalone-resolutions';
import { BrandsAudioServicesProvider } from '../context/brands-audio-services-provider';
import { useClientId } from '../../../hooks/use-client-id';
import { getBrandKit } from '../../../clients/brand-kit-registry';
import { useEditorUpload } from '../../../hooks/use-editor-upload';

export interface CreativityEditorProps {
  initialJSON?: CanvasJSON;
}

export const CreativityEditorPage: React.FC<CreativityEditorProps> = () => {
  const editorRef = useRef<CreativityEditorHandle>(null);
  const { saveProject, updateProject, getProject, projects } = useProjectStorage();
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [activeResolutionId, setActiveResolutionId] = useState(STANDALONE_RESOLUTIONS[0].id);
  const clientId = useClientId();
  const brandKit = useMemo(() => getBrandKit(clientId), [clientId]);
  const uploadMedia = useEditorUpload('creativity');

  const activeRes = STANDALONE_RESOLUTIONS.find(r => r.id === activeResolutionId) ?? STANDALONE_RESOLUTIONS[0];

  const handleSave = useCallback(
    (canvasJSON: CanvasJSON) => {
      if (currentProjectId) {
        updateProject(currentProjectId, { canvasJSON });
        return;
      }
      const project: SavedProject = {
        id: generateId(),
        canvasJSON,
        width: activeRes.canvasWidth,
        height: activeRes.canvasHeight
      };
      saveProject(project);
      setCurrentProjectId(project.id);
    },
    [currentProjectId, activeRes.canvasWidth, activeRes.canvasHeight, saveProject, updateProject]
  );

  const handleSelectProject = useCallback(
    (projectId: string) => {
      const project = getProject(projectId);
      if (!project) return;
      setCurrentProjectId(projectId);
      editorRef.current?.loadProject(project.canvasJSON);
    },
    [getProject]
  );

  return (
    <BrandsAudioServicesProvider>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          backgroundColor: C.appBg,
          overflow: 'hidden'
        }}
      >
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Sidebar project={{ currentId: currentProjectId, projects, onSelect: handleSelectProject }} />
          <CreativityEditorCore
            ref={editorRef}
            initialCanvasJSON={STANDALONE_RESOLUTIONS[0].layout}
            resolutions={STANDALONE_RESOLUTIONS}
            activeResolutionId={activeResolutionId}
            onResolutionChange={setActiveResolutionId}
            kind='editor'
            host='creativity'
            brandKit={brandKit}
            uploadMedia={uploadMedia}
            onSave={handleSave}
          />
        </div>
      </div>
    </BrandsAudioServicesProvider>
  );
};
